import { supabase } from '@/integrations/supabase/client';
import { pipeline } from '@huggingface/transformers';

let embeddingPipeline: any = null;

async function getEmbeddingPipeline() {
  if (embeddingPipeline) return embeddingPipeline;
  console.log('[Embeddings] Loading Hugging Face pipeline...');
  try {
    embeddingPipeline = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
    console.log('[Embeddings] Pipeline loaded successfully:', embeddingPipeline);
    return embeddingPipeline;
  } catch (err) {
    console.error('[Embeddings] Error loading pipeline:', err);
    throw err;
  }
}

function ensureFlatArray(arr: any): number[] | null {
  if (!Array.isArray(arr)) return null;
  // If it's a nested array (e.g., [ [ ... ] ]), return the first element
  if (Array.isArray(arr[0])) {
    return arr[0];
  }
  // If it's already flat, return as is
  return arr;
}

async function generateEmbeddingMiniLM384(text: string): Promise<number[] | null> {
  try {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      console.warn('[Embeddings] Empty or invalid text input for embedding:', text);
      return null;
    }
    const pipe = await getEmbeddingPipeline();
    const output = await pipe(text, { pooling: 'mean', normalize: true });

    // Handle Tensor output (Transformers.js returns a Proxy(Tensor))
    if (output && typeof output === 'object' && (output.data || output.tolist || output.ort_tensor)) {
      // Try .data() (async)
      if (typeof output.data === 'function') {
        const arr = await output.data();
        const flat = ensureFlatArray(arr);
        if (flat) return flat;
      }
      // Try .tolist() (sync)
      if (typeof output.tolist === 'function') {
        const arr = output.tolist();
        const flat = ensureFlatArray(arr);
        if (flat) return flat;
      }
      // Try .ort_tensor.data
      if (output.ort_tensor && Array.isArray(output.ort_tensor.data)) {
        const flat = ensureFlatArray(output.ort_tensor.data);
        if (flat) return flat;
      }
    }

    // Handle array output (legacy)
    if (Array.isArray(output)) {
      const flat = ensureFlatArray(output);
      if (flat) return flat;
    }

    return null;
  } catch (error: any) {
    console.error('[Embeddings] Error generating embedding with Transformers.js:', error);
    return null;
  }
}

/**
 * Legacy embedding helper (MiniLM ~384 dims).
 * NOTE: Do not use this for Supabase pgvector (1536 dims) until the cloud schema is migrated.
 */
export async function generateEmbedding(text: string): Promise<number[] | null> {
  const emb = await generateEmbeddingMiniLM384(text);
  if (emb && emb.length !== 384) {
    console.warn('[Embeddings] Unexpected MiniLM embedding length:', emb.length);
  }
  return emb;
}

/**
 * Explicit 384-d embedding for local (RuVector) search.
 */
export async function generateEmbedding384(text: string): Promise<number[] | null> {
  const emb = await generateEmbeddingMiniLM384(text);
  if (!emb) return null;
  if (emb.length !== 384) {
    console.warn('[Embeddings] Expected 384-d embedding but got:', emb.length);
    return null;
  }
  return emb;
}

/**
 * 1536-d embedding for cloud (Supabase pgvector).
 * We generate this via the server-side OpenAI proxy so the web build doesn't need any secrets.
 */
export async function generateEmbedding1536(text: string): Promise<number[] | null> {
  try {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      return null;
    }

    const { data, error } = await supabase.functions.invoke('openai-proxy', {
      body: {
        endpoint: 'embeddings',
        body: {
          model: 'text-embedding-3-small',
          input: text,
        },
      },
    });

    if (error) {
      console.error('[Embeddings] openai-proxy embedding error:', error);
      return null;
    }

    const embedding = data?.data?.[0]?.embedding;
    if (!Array.isArray(embedding)) {
      console.error('[Embeddings] openai-proxy returned unexpected payload:', data);
      return null;
    }

    if (embedding.length !== 1536) {
      console.warn('[Embeddings] Expected 1536-d embedding but got:', embedding.length);
      return null;
    }

    return embedding as number[];
  } catch (e: any) {
    console.error('[Embeddings] Error generating 1536-d embedding:', e);
    return null;
  }
}

export async function expandQueryWithLLM(query: string): Promise<string[]> {
  return [query];
}

export async function checkKnowledgeBaseEmbeddings(knowledgeBaseId: string): Promise<{
  total: number;
  withEmbeddings: number;
  embeddingDimensions: number | null;
}> {
  try {
    const { data: documents, error } = await supabase
      .from('knowledge_base_documents')
      .select('*')
      .eq('knowledge_base_id', knowledgeBaseId);

    if (error) {
      throw error;
    }

    if (!documents || documents.length === 0) {
      return {
        total: 0,
        withEmbeddings: 0,
        embeddingDimensions: null
      };
    }

    let withEmbeddings = 0;
    let embeddingDimensions = null;

    for (const doc of documents) {
      if ('embedding' in doc && doc.embedding !== null) {
        withEmbeddings++;
        if (embeddingDimensions === null && Array.isArray(doc.embedding)) {
          embeddingDimensions = doc.embedding.length;
        }
      }
    }

    return {
      total: documents.length,
      withEmbeddings,
      embeddingDimensions
    };
  } catch (error) {
    console.error('Error checking knowledge base embeddings:', error);
    return {
      total: 0,
      withEmbeddings: 0,
      embeddingDimensions: null
    };
  }
}